import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Paper,
  LinearProgress,
  Chip,
  Alert,
  Divider,
  Collapse,
  IconButton,
  Tooltip,
  Table,
  CircularProgress,
  Skeleton,
  Fade,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Radio,
  TableSortLabel
} from '@mui/material';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TimelineIcon from '@mui/icons-material/Timeline';
import BarChartIcon from '@mui/icons-material/BarChart';
import SearchIcon from '@mui/icons-material/Search';
import GroupIcon from '@mui/icons-material/Group';
import TuneIcon from '@mui/icons-material/Tune';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SensitivityBadge from './SensitivityBadge';

/**
 * DataExplorationDetail Component (Redesigned)
 * User-focused data exploration section with clear methodology
 *
 * @param {object} props
 * @param {object} props.data - Frequency and usage data
 * @param {string} props.sensitivity - Sensitivity level
 * @param {string} props.usagePattern - Usage pattern description
 * @param {string} props.parameterName - Parameter name for context
 */
const DataExplorationDetail = ({ data, sensitivity, usagePattern, parameterName = 'Parameter' }) => {
  const navigate = useNavigate();

  const {
    caseCoverage = 0,
    totalCases = 0,
    casesWithParameter = 0,
    stationCoverage = [],
    avgMeasurementsPerCase = 0,
    medianMeasurementsPerCase = 0,
    totalMeasurements = 0,
    measurementPattern = {},
    continuousCases = 0,
    singleMeasurementCases = 0,
    availableSince = 'N/A'
  } = data;

  // State for expandable sections
  const [expandedGroups, setExpandedGroups] = useState({});

  // State for selected station group (for filtering usage characteristics)
  const [selectedStationGroup, setSelectedStationGroup] = useState(null);

  // State for similar parameters table sorting
  const [sortBy, setSortBy] = useState('similarityScore');
  const [sortOrder, setSortOrder] = useState('desc');

  // State for loading transition
  const [isLoadingData, setIsLoadingData] = useState(false);

  // State for showing filter alert (one-time after first selection)
  const [showFilterAlert, setShowFilterAlert] = useState(false);

  // Group stations by category (based on naming convention)
  const groupStationsByType = (stations) => {
    const groups = {
      'Intensivstationen': [],
      'IMC (Intermediate Care)': [],
      'Normalstationen': [],
      'OP & Diagnostik': [],
      'Labor': [],
      'Sonstige': []
    };

    stations.forEach(station => {
      if (station.name.includes('ICU') || station.name.includes('NICU') || station.name.includes('PICU')) {
        groups['Intensivstationen'].push(station);
      } else if (station.name.includes('IMC')) {
        groups['IMC (Intermediate Care)'].push(station);
      } else if (station.name.includes('Station') || station.name.includes('Stroke Unit')) {
        groups['Normalstationen'].push(station);
      } else if (station.name.includes('OP') || station.name.includes('Radiologie') || station.name.includes('Endoskopie') || station.name.includes('Notaufnahme')) {
        groups['OP & Diagnostik'].push(station);
      } else if (station.name.includes('Labor') || station.name.includes('Blutbank')) {
        groups['Labor'].push(station);
      } else {
        groups['Sonstige'].push(station);
      }
    });

    // Remove empty groups
    return Object.entries(groups)
      .filter(([_, stations]) => stations.length > 0)
      .map(([groupName, stations]) => {
        // Calculate group statistics
        const totalGroupCases = stations.reduce((sum, s) => sum + (s.caseCount || 0), 0);
        const avgGroupCoverage = stations.reduce((sum, s) => sum + s.percentage, 0) / stations.length;

        return {
          name: groupName,
          stations: stations.filter(s => (s.caseCount || 0) >= 30), // Privacy: only show stations with n>=30
          totalCases: totalGroupCases,
          avgCoverage: avgGroupCoverage,
          stationCount: stations.length
        };
      });
  };

  const stationGroups = groupStationsByType(stationCoverage);

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  // Handle station group selection with loading state
  const handleStationGroupChange = (groupName) => {
    setIsLoadingData(true);
    setSelectedStationGroup(groupName);

    // Show filter alert on first selection
    if (groupName && !showFilterAlert) {
      setShowFilterAlert(true);
    }

    // Simulate API call delay
    setTimeout(() => {
      setIsLoadingData(false);
    }, 300);
  };

  // Calculate continuous case percentage
  const continuousCasePercentage = casesWithParameter > 0
    ? ((continuousCases / casesWithParameter) * 100).toFixed(0)
    : 0;

  // Get filtered measurement frequency data based on selected station group
  const getFilteredMeasurementFrequency = (selectedGroup) => {
    // In production, this would call an API endpoint with the selected group
    // Mock data simulating different measurement patterns per station group

    if (!selectedGroup) {
      // All areas aggregated
      return [
        { label: '15min', percent: 5, color: '#e3f2fd' },
        { label: '1h', percent: 65, color: '#1976d2' },
        { label: '4h', percent: 20, color: '#42a5f5' },
        { label: '12h', percent: 8, color: '#90caf9' },
        { label: '24h', percent: 2, color: '#bbdefb' }
      ];
    }

    // Station-group specific patterns
    const groupPatterns = {
      'Intensivstationen': [
        { label: '15min', percent: 15, color: '#e3f2fd' },
        { label: '1h', percent: 75, color: '#1976d2' },
        { label: '4h', percent: 8, color: '#42a5f5' },
        { label: '12h', percent: 2, color: '#90caf9' },
        { label: '24h', percent: 0, color: '#bbdefb' }
      ],
      'IMC (Intermediate Care)': [
        { label: '15min', percent: 3, color: '#e3f2fd' },
        { label: '1h', percent: 45, color: '#1976d2' },
        { label: '4h', percent: 40, color: '#42a5f5' },
        { label: '12h', percent: 10, color: '#90caf9' },
        { label: '24h', percent: 2, color: '#bbdefb' }
      ],
      'Normalstationen': [
        { label: '15min', percent: 0, color: '#e3f2fd' },
        { label: '1h', percent: 10, color: '#1976d2' },
        { label: '4h', percent: 30, color: '#42a5f5' },
        { label: '12h', percent: 45, color: '#90caf9' },
        { label: '24h', percent: 15, color: '#bbdefb' }
      ],
      'OP & Diagnostik': [
        { label: '15min', percent: 25, color: '#e3f2fd' },
        { label: '1h', percent: 50, color: '#1976d2' },
        { label: '4h', percent: 20, color: '#42a5f5' },
        { label: '12h', percent: 5, color: '#90caf9' },
        { label: '24h', percent: 0, color: '#bbdefb' }
      ],
      'Labor': [
        { label: '15min', percent: 0, color: '#e3f2fd' },
        { label: '1h', percent: 5, color: '#1976d2' },
        { label: '4h', percent: 15, color: '#42a5f5' },
        { label: '12h', percent: 60, color: '#90caf9' },
        { label: '24h', percent: 20, color: '#bbdefb' }
      ]
    };

    return groupPatterns[selectedGroup] || groupPatterns['Intensivstationen'];
  };

  // Get filtered measurements per case distribution based on selected station group
  const getFilteredMeasurementsPerCase = (selectedGroup) => {
    // In production, this would call an API endpoint with the selected group

    if (!selectedGroup) {
      // All areas aggregated
      return [
        { label: 'Einzelmessung (1)', percent: 15, color: '#ffcdd2' },
        { label: 'Wenige (2-10)', percent: 20, color: '#bbdefb' },
        { label: 'Mittel (11-50)', percent: 45, color: '#90caf9' },
        { label: 'Viele (>50)', percent: 20, color: '#42a5f5' }
      ];
    }

    const groupPatterns = {
      'Intensivstationen': [
        { label: 'Einzelmessung (1)', percent: 5, color: '#ffcdd2' },
        { label: 'Wenige (2-10)', percent: 10, color: '#bbdefb' },
        { label: 'Mittel (11-50)', percent: 35, color: '#90caf9' },
        { label: 'Viele (>50)', percent: 50, color: '#42a5f5' }
      ],
      'IMC (Intermediate Care)': [
        { label: 'Einzelmessung (1)', percent: 10, color: '#ffcdd2' },
        { label: 'Wenige (2-10)', percent: 25, color: '#bbdefb' },
        { label: 'Mittel (11-50)', percent: 50, color: '#90caf9' },
        { label: 'Viele (>50)', percent: 15, color: '#42a5f5' }
      ],
      'Normalstationen': [
        { label: 'Einzelmessung (1)', percent: 30, color: '#ffcdd2' },
        { label: 'Wenige (2-10)', percent: 40, color: '#bbdefb' },
        { label: 'Mittel (11-50)', percent: 25, color: '#90caf9' },
        { label: 'Viele (>50)', percent: 5, color: '#42a5f5' }
      ],
      'OP & Diagnostik': [
        { label: 'Einzelmessung (1)', percent: 20, color: '#ffcdd2' },
        { label: 'Wenige (2-10)', percent: 50, color: '#bbdefb' },
        { label: 'Mittel (11-50)', percent: 25, color: '#90caf9' },
        { label: 'Viele (>50)', percent: 5, color: '#42a5f5' }
      ],
      'Labor': [
        { label: 'Einzelmessung (1)', percent: 40, color: '#ffcdd2' },
        { label: 'Wenige (2-10)', percent: 35, color: '#bbdefb' },
        { label: 'Mittel (11-50)', percent: 20, color: '#90caf9' },
        { label: 'Viele (>50)', percent: 5, color: '#42a5f5' }
      ]
    };

    return groupPatterns[selectedGroup] || groupPatterns['Intensivstationen'];
  };

  // Prepare data for radar chart (parameter quality profile)
  const getQualityProfileData = () => {
    // Calculate scores (0-100) for different quality dimensions
    const yearsAvailable = availableSince ? new Date().getFullYear() - parseInt(availableSince.split('-')[0]) : 0;
    const timeScore = Math.min(100, (yearsAvailable / 10) * 100); // 10 years = 100%

    return [
      {
        dimension: 'Fallabdeckung',
        value: caseCoverage,
        fullMark: 100,
        description: 'Prozentsatz der Fälle mit mindestens einer Messung'
      },
      {
        dimension: 'Vollständigkeit',
        value: 95, // From data quality section - could be dynamic
        fullMark: 100,
        description: 'Anteil vollständiger Messungen ohne fehlende Werte'
      },
      {
        dimension: 'Verfügbarkeit',
        value: timeScore,
        fullMark: 100,
        description: `Seit ${yearsAvailable} Jahren verfügbar (max. 10 Jahre = 100%)`
      },
      {
        dimension: 'Stationsbreite',
        value: Math.min(100, (stationGroups.length / 6) * 100), // 6+ groups = 100%
        fullMark: 100,
        description: `Auf ${stationGroups.length} Stationsgruppen verfügbar (6+ = 100%)`
      },
      {
        dimension: 'Kontinuität',
        value: parseInt(continuousCasePercentage),
        fullMark: 100,
        description: 'Prozentsatz der Fälle mit ≥10 Messungen (kontinuierliche Erfassung)'
      },
    ];
  };

  // Custom tooltip for radar chart
  const CustomRadarTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper sx={{ p: 1.5, maxWidth: 250 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            {data.dimension}
          </Typography>
          <Typography variant="body2" color="primary" fontWeight="bold" sx={{ mb: 0.5 }}>
            {Math.round(data.value)}%
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {data.description}
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  // Handle sort for similar parameters table
  const handleSortRequest = (property) => {
    const isAsc = sortBy === property && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  // Sort similar parameters
  const sortSimilarParameters = (parameters) => {
    return [...parameters].sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Handle string comparison
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  };

  // Get filtered data quality metrics based on selected station group
  const getFilteredDataQuality = (selectedGroup) => {
    // In production, this would call an API endpoint with the selected group
    // Mock data simulating different quality patterns per station group

    if (!selectedGroup) {
      // All areas aggregated
      return {
        completeness: { value: 95, missing: 5, pattern: 'MCAR' },
        plausibility: { value: 99.2, outsideRange: 0.8 },
        units: [
          { unit: 'mmHg', percent: 82, isPrimary: true },
          { unit: 'kPa', percent: 15, isPrimary: false },
          { unit: 'cmH₂O', percent: 3, isPrimary: false }
        ],
        outliers: {
          iqr: { percent: 8.2, lower: 75, upper: 180 },
          zscore: { percent: 2.1, lower: 60, upper: 200 },
          physiological: { percent: 0.8, lower: 40, upper: 260 }
        }
      };
    }

    // Station-group specific quality metrics
    const groupMetrics = {
      'Intensivstationen': {
        completeness: { value: 97, missing: 3, pattern: 'MCAR' },
        plausibility: { value: 99.5, outsideRange: 0.5 },
        units: [
          { unit: 'mmHg', percent: 95, isPrimary: true },
          { unit: 'kPa', percent: 5, isPrimary: false },
          { unit: 'cmH₂O', percent: 0, isPrimary: false }
        ],
        outliers: {
          iqr: { percent: 12.5, lower: 70, upper: 190 },
          zscore: { percent: 3.8, lower: 55, upper: 210 },
          physiological: { percent: 1.2, lower: 40, upper: 260 }
        }
      },
      'IMC (Intermediate Care)': {
        completeness: { value: 94, missing: 6, pattern: 'MCAR' },
        plausibility: { value: 99.0, outsideRange: 1.0 },
        units: [
          { unit: 'mmHg', percent: 88, isPrimary: true },
          { unit: 'kPa', percent: 12, isPrimary: false },
          { unit: 'cmH₂O', percent: 0, isPrimary: false }
        ],
        outliers: {
          iqr: { percent: 9.5, lower: 72, upper: 185 },
          zscore: { percent: 2.5, lower: 58, upper: 205 },
          physiological: { percent: 0.9, lower: 40, upper: 260 }
        }
      },
      'Normalstationen': {
        completeness: { value: 88, missing: 12, pattern: 'MCAR' },
        plausibility: { value: 98.5, outsideRange: 1.5 },
        units: [
          { unit: 'mmHg', percent: 65, isPrimary: true },
          { unit: 'kPa', percent: 30, isPrimary: false },
          { unit: 'cmH₂O', percent: 5, isPrimary: false }
        ],
        outliers: {
          iqr: { percent: 6.8, lower: 78, upper: 175 },
          zscore: { percent: 1.5, lower: 65, upper: 195 },
          physiological: { percent: 0.4, lower: 40, upper: 260 }
        }
      },
      'OP & Diagnostik': {
        completeness: { value: 96, missing: 4, pattern: 'MCAR' },
        plausibility: { value: 99.3, outsideRange: 0.7 },
        units: [
          { unit: 'mmHg', percent: 90, isPrimary: true },
          { unit: 'kPa', percent: 8, isPrimary: false },
          { unit: 'cmH₂O', percent: 2, isPrimary: false }
        ],
        outliers: {
          iqr: { percent: 10.2, lower: 68, upper: 195 },
          zscore: { percent: 3.1, lower: 52, upper: 215 },
          physiological: { percent: 1.0, lower: 40, upper: 260 }
        }
      },
      'Labor': {
        completeness: { value: 92, missing: 8, pattern: 'MAR' },
        plausibility: { value: 98.8, outsideRange: 1.2 },
        units: [
          { unit: 'mmHg', percent: 70, isPrimary: true },
          { unit: 'kPa', percent: 25, isPrimary: false },
          { unit: 'cmH₂O', percent: 5, isPrimary: false }
        ],
        outliers: {
          iqr: { percent: 7.5, lower: 76, upper: 178 },
          zscore: { percent: 1.8, lower: 62, upper: 198 },
          physiological: { percent: 0.6, lower: 40, upper: 260 }
        }
      }
    };

    return groupMetrics[selectedGroup] || groupMetrics['Intensivstationen'];
  };

  // Get filtered similar parameters based on selected station group
  const getFilteredSimilarParameters = (selectedGroup) => {
    // In production, this would call an API endpoint with the selected group
    // Mock data simulating different similar parameters per station group

    if (!selectedGroup) {
      // All areas aggregated - return default similar parameters
      return [
        {
          id: 2,
          name: 'Herzfrequenz',
          description: 'Herzschläge pro Minute, kontinuierliche Messung',
          caseCoverage: 87,
          frequency: 'Stündlich',
          stationCount: 12,
          similarityScore: 92,
          similarityReasons: ['Gleiche Messfrequenz', 'Ähnliche Stationsnutzung', 'Kontinuierliche Erfassung']
        },
        {
          id: 3,
          name: 'SpO2 (Sauerstoffsättigung)',
          description: 'Periphere Sauerstoffsättigung in %, kontinuierliche Messung',
          caseCoverage: 84,
          frequency: 'Stündlich',
          stationCount: 11,
          similarityScore: 89,
          similarityReasons: ['Gleiche Messfrequenz', 'Ähnliche Stationsnutzung', 'Vitalparameter']
        },
        {
          id: 5,
          name: 'Blutdruck diastolisch',
          description: 'Diastolischer Blutdruck in mmHg, gemessen am Oberarm',
          caseCoverage: 85,
          frequency: 'Stündlich',
          stationCount: 12,
          similarityScore: 95,
          similarityReasons: ['Gleiche Messfrequenz', 'Identische Stationsnutzung', 'Zusammengehörige Messung']
        },
        {
          id: 7,
          name: 'Temperatur',
          description: 'Körpertemperatur in °C, regelmäßige Messung',
          caseCoverage: 88,
          frequency: '4-stündlich',
          stationCount: 14,
          similarityScore: 78,
          similarityReasons: ['Ähnliche Stationsnutzung', 'Vitalparameter', 'Regelmäßige Erfassung']
        }
      ];
    }

    // Station-group specific similar parameters
    const groupSimilarParams = {
      'Intensivstationen': [
        {
          id: 2,
          name: 'Herzfrequenz',
          description: 'Kontinuierliches Monitoring auf ICU',
          caseCoverage: 95,
          frequency: 'Kontinuierlich',
          stationCount: 8,
          similarityScore: 98,
          similarityReasons: ['Identisches Monitoring-System', 'Kontinuierliche Erfassung', 'ICU-Vitalparameter']
        },
        {
          id: 5,
          name: 'Blutdruck diastolisch',
          description: 'Invasive & nicht-invasive Messung',
          caseCoverage: 93,
          frequency: 'Kontinuierlich',
          stationCount: 8,
          similarityScore: 97,
          similarityReasons: ['Zusammengehörige Messung', 'Identische ICU-Stationen', 'Gleiche Frequenz']
        },
        {
          id: 3,
          name: 'SpO2 (Sauerstoffsättigung)',
          description: 'Kontinuierliches Monitoring',
          caseCoverage: 96,
          frequency: 'Kontinuierlich',
          stationCount: 8,
          similarityScore: 96,
          similarityReasons: ['Identisches Monitoring-System', 'ICU-Standard', 'Kontinuierlich']
        }
      ],
      'IMC (Intermediate Care)': [
        {
          id: 2,
          name: 'Herzfrequenz',
          description: 'Regelmäßiges Monitoring auf IMC',
          caseCoverage: 88,
          frequency: 'Stündlich',
          stationCount: 4,
          similarityScore: 90,
          similarityReasons: ['Gleiche Messfrequenz', 'IMC-Vitalparameter', 'Ähnliche Stationsnutzung']
        },
        {
          id: 3,
          name: 'SpO2 (Sauerstoffsättigung)',
          description: 'Regelmäßige Messung',
          caseCoverage: 85,
          frequency: 'Stündlich',
          stationCount: 4,
          similarityScore: 88,
          similarityReasons: ['Gleiche Messfrequenz', 'IMC-Standard', 'Vitalparameter']
        },
        {
          id: 5,
          name: 'Blutdruck diastolisch',
          description: 'Nicht-invasive Messung',
          caseCoverage: 86,
          frequency: 'Stündlich',
          stationCount: 4,
          similarityScore: 92,
          similarityReasons: ['Zusammengehörige Messung', 'Identische IMC-Stationen', 'Gleiche Frequenz']
        }
      ],
      'Normalstationen': [
        {
          id: 7,
          name: 'Temperatur',
          description: 'Regelmäßige Kontrolle',
          caseCoverage: 92,
          frequency: '4-stündlich',
          stationCount: 18,
          similarityScore: 85,
          similarityReasons: ['Ähnliche Messfrequenz', 'Basisvitalparameter', 'Stationsbreite']
        },
        {
          id: 2,
          name: 'Herzfrequenz',
          description: 'Sporadische Messung',
          caseCoverage: 65,
          frequency: '4-stündlich',
          stationCount: 18,
          similarityScore: 75,
          similarityReasons: ['Ähnliche Messfrequenz', 'Vitalparameter', 'Normalstations-Routine']
        },
        {
          id: 3,
          name: 'SpO2 (Sauerstoffsättigung)',
          description: 'Bei Bedarf',
          caseCoverage: 58,
          frequency: 'Unregelmäßig',
          stationCount: 15,
          similarityScore: 60,
          similarityReasons: ['Ähnliche Stationsnutzung', 'Vitalparameter']
        }
      ],
      'OP & Diagnostik': [
        {
          id: 2,
          name: 'Herzfrequenz',
          description: 'Intraoperatives Monitoring',
          caseCoverage: 98,
          frequency: 'Kontinuierlich',
          stationCount: 6,
          similarityScore: 95,
          similarityReasons: ['Intraop-Standard', 'Kontinuierliche Erfassung', 'Identische OP-Säle']
        },
        {
          id: 3,
          name: 'SpO2 (Sauerstoffsättigung)',
          description: 'Intraoperatives Monitoring',
          caseCoverage: 99,
          frequency: 'Kontinuierlich',
          stationCount: 6,
          similarityScore: 96,
          similarityReasons: ['Intraop-Standard', 'Anästhesie-Pflicht', 'Kontinuierlich']
        },
        {
          id: 5,
          name: 'Blutdruck diastolisch',
          description: 'Intraoperatives Monitoring',
          caseCoverage: 97,
          frequency: 'Kontinuierlich',
          stationCount: 6,
          similarityScore: 98,
          similarityReasons: ['Zusammengehörige Messung', 'Intraop-Standard', 'Identische OP-Säle']
        }
      ],
      'Labor': [
        {
          id: 10,
          name: 'Kalium',
          description: 'Laborchemischer Parameter',
          caseCoverage: 78,
          frequency: '12-24h',
          stationCount: 2,
          similarityScore: 70,
          similarityReasons: ['Laborwert', 'Ähnliche Abnahmefrequenz', 'Elektrolyt-Panel']
        },
        {
          id: 11,
          name: 'Natrium',
          description: 'Laborchemischer Parameter',
          caseCoverage: 80,
          frequency: '12-24h',
          stationCount: 2,
          similarityScore: 72,
          similarityReasons: ['Laborwert', 'Ähnliche Abnahmefrequenz', 'Elektrolyt-Panel']
        }
      ]
    };

    return groupSimilarParams[selectedGroup] || groupSimilarParams['Intensivstationen'];
  };

  /**
   * Find similar parameters based on measurement pattern, station usage, and usage pattern
   * In production, this would call an API endpoint
   */
  const getSimilarParameters = (pattern, stations, usage) => {
    // Mock similar parameters - in production, this would be calculated server-side
    const mockSimilarParams = [
      {
        id: 2,
        name: 'Herzfrequenz',
        description: 'Herzschläge pro Minute, kontinuierliche Messung',
        caseCoverage: 87,
        frequency: 'Stündlich',
        stationCount: 12,
        similarityScore: 92,
        similarityReasons: ['Gleiche Messfrequenz', 'Ähnliche Stationsnutzung', 'Kontinuierliche Erfassung']
      },
      {
        id: 3,
        name: 'SpO2 (Sauerstoffsättigung)',
        description: 'Periphere Sauerstoffsättigung in %, kontinuierliche Messung',
        caseCoverage: 84,
        frequency: 'Stündlich',
        stationCount: 11,
        similarityScore: 89,
        similarityReasons: ['Gleiche Messfrequenz', 'Ähnliche Stationsnutzung', 'Vitalparameter']
      },
      {
        id: 5,
        name: 'Blutdruck diastolisch',
        description: 'Diastolischer Blutdruck in mmHg, gemessen am Oberarm',
        caseCoverage: 85,
        frequency: 'Stündlich',
        stationCount: 12,
        similarityScore: 95,
        similarityReasons: ['Gleiche Messfrequenz', 'Identische Stationsnutzung', 'Zusammengehörige Messung']
      },
      {
        id: 7,
        name: 'Temperatur',
        description: 'Körpertemperatur in °C, regelmäßige Messung',
        caseCoverage: 88,
        frequency: '4-stündlich',
        stationCount: 14,
        similarityScore: 78,
        similarityReasons: ['Ähnliche Stationsnutzung', 'Vitalparameter', 'Regelmäßige Erfassung']
      }
    ];

    // In production, calculate similarity based on:
    // - Measurement frequency (pattern.typical)
    // - Station overlap (stations)
    // - Usage pattern (usage)
    // - OMOP domain
    // - Tags/categories

    return mockSimilarParams;
  };

  return (
    <Box>
      {/* Hero Section: Key Metrics */}
      <Paper
        elevation={0}
        sx={{
          px: 3,
          py: 1,
          mb: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }}
      >
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Verfügbarkeit & Nutzung
        </Typography>

        <Grid container spacing={2} sx={{ mt: 0.5, alignItems: 'center' }}>
          {/* Left side: Metrics in 2x2 Grid */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 0.5 }}>
              {/* First Row */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        Fälle
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {casesWithParameter.toLocaleString()}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    height: 'auto',
                    px: 2,
                    py: 0.75,
                    flex: 1,
                    justifyContent: 'space-between',
                    '& .MuiChip-label': {
                      width: '100%',
                      px: 0
                    }
                  }}
                />
                <Chip
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        Abdeckung
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {caseCoverage}%
                      </Typography>
                    </Box>
                  }
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    height: 'auto',
                    px: 2,
                    py: 0.75,
                    flex: 1,
                    justifyContent: 'space-between',
                    '& .MuiChip-label': {
                      width: '100%',
                      px: 0
                    }
                  }}
                />
              </Box>

              {/* Second Row */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        Stationsgruppen
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {stationGroups.length}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    height: 'auto',
                    px: 2,
                    py: 0.75,
                    flex: 1,
                    justifyContent: 'space-between',
                    '& .MuiChip-label': {
                      width: '100%',
                      px: 0
                    }
                  }}
                />
                <Chip
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        Verfügbar seit
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {availableSince}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    height: 'auto',
                    px: 2,
                    py: 0.75,
                    flex: 1,
                    justifyContent: 'space-between',
                    '& .MuiChip-label': {
                      width: '100%',
                      px: 0
                    }
                  }}
                />
              </Box>
            </Box>
          </Grid>

          {/* Right side: Radar Chart */}
          <Grid item xs={12} md={6}>
            <Box sx={{
              height: { xs: 220, md: 250 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Typography variant="subtitle2" sx={{ opacity: 0.95, mb: 1, fontWeight: 600 }}>
                Qualitätsprofil
              </Typography>
              <Box sx={{ width: '100%', height: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={getQualityProfileData()} outerRadius="65%">
                    <PolarGrid stroke="rgba(255,255,255,0.3)" />
                    <PolarAngleAxis
                      dataKey="dimension"
                      tick={{ fill: 'white', fontSize: 12 }}
                    />
                    <PolarRadiusAxis
                      angle={90}
                      domain={[0, 100]}
                      tick={{ fill: 'white', fontSize: 9 }}
                      tickCount={6}
                    />
                    <RechartsTooltip content={<CustomRadarTooltip />} />
                    <Radar
                      name="Qualität"
                      dataKey="value"
                      stroke="#ffffff"
                      strokeWidth={2.5}
                      fill="rgba(255,255,255,0.6)"
                      fillOpacity={0.5}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Sticky Filter Bar (when filter is active) */}
      {selectedStationGroup && (
        <Paper
          elevation={3}
          sx={{
            position: 'sticky',
            top: 64, // Adjust based on your header height
            zIndex: 100,
            p: 1.5,
            mb: 2,
            backgroundColor: '#e3f2fd',
            borderLeft: '4px solid #1976d2',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterAltIcon color="primary" />
            <Typography variant="body2" fontWeight="bold" color="primary">
              Aktiver Filter:
            </Typography>
            <Chip
              label={selectedStationGroup}
              color="primary"
              onDelete={() => handleStationGroupChange(null)}
              sx={{ fontWeight: 'bold' }}
            />
          </Box>
          <Typography variant="caption" color="textSecondary">
            Alle nachfolgenden Ansichten sind auf diesen Bereich gefiltert
          </Typography>
        </Paper>
      )}

      {/* Einmal-Alert nach erster Auswahl */}
      {showFilterAlert && selectedStationGroup && (
        <Fade in={showFilterAlert} timeout={500}>
          <Alert
            severity="info"
            onClose={() => setShowFilterAlert(false)}
            sx={{ mb: 2 }}
          >
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              Filter aktiviert!
            </Typography>
            <Typography variant="caption">
              Die Sektionen <strong>Nutzungs-Charakteristik</strong>, <strong>Datenqualität</strong> und{' '}
              <strong>Ähnliche Parameter</strong> zeigen jetzt ausschließlich Daten für{' '}
              <strong>{selectedStationGroup}</strong>. Klicken Sie auf das ×-Symbol im Filter-Chip,
              um zur Gesamtansicht zurückzukehren.
            </Typography>
          </Alert>
        </Fade>
      )}

      {/* Section 1: Verfügbarkeit nach Bereich */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <TuneIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h6" fontWeight="bold">
            Datenfilter: Verfügbarkeit nach Bereich
          </Typography>
          <Tooltip title="Einzelne Stationen werden nur angezeigt, wenn mindestens 30 Fälle vorliegen (Datenschutz)">
            <IconButton size="small">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Alert severity="info" icon={<TuneIcon />} sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Wählen Sie einen Bereich aus</strong>, um alle nachfolgenden Ansichten{' '}
            (Nutzungs-Charakteristik, Datenqualität, Ähnliche Parameter) auf diesen Bereich zu filtern.
          </Typography>
        </Alert>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" width="50px"></TableCell>
                <TableCell>Bereich</TableCell>
                <TableCell align="center">Stationen</TableCell>
                <TableCell align="center">Fälle</TableCell>
                <TableCell>Abdeckung</TableCell>
                <TableCell align="center" width="60px">Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* "Alle Bereiche" row */}
              <TableRow
                hover
                sx={{
                  cursor: 'pointer',
                  backgroundColor: selectedStationGroup === null ? '#e3f2fd' : 'inherit',
                  '&:hover': {
                    backgroundColor: selectedStationGroup === null ? '#e3f2fd !important' : undefined
                  }
                }}
                onClick={() => handleStationGroupChange(null)}
              >
                <TableCell padding="checkbox">
                  <Radio
                    checked={selectedStationGroup === null}
                    onChange={() => handleStationGroupChange(null)}
                    value="all"
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    Alle Bereiche
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={stationGroups.reduce((sum, g) => sum + g.stations.length, 0)}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">
                    {stationGroups.reduce((sum, g) => sum + g.totalCases, 0).toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={caseCoverage}
                      sx={{
                        flex: 1,
                        height: 8,
                        borderRadius: 1,
                        backgroundColor: '#e0e0e0',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: caseCoverage >= 80 ? '#4caf50' : caseCoverage >= 50 ? '#ff9800' : '#f44336'
                        }
                      }}
                    />
                    <Typography variant="body2" sx={{ minWidth: 45 }}>
                      {Math.round(caseCoverage)}%
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="caption" color="textSecondary">-</Typography>
                </TableCell>
              </TableRow>

              {/* Station groups */}
              {stationGroups.map((group, idx) => (
                <React.Fragment key={idx}>
                  <TableRow
                    hover
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: selectedStationGroup === group.name ? '#e3f2fd' : 'inherit',
                      '&:hover': {
                        backgroundColor: selectedStationGroup === group.name ? '#e3f2fd !important' : undefined
                      }
                    }}
                    onClick={() => handleStationGroupChange(group.name)}
                  >
                    <TableCell padding="checkbox">
                      <Radio
                        checked={selectedStationGroup === group.name}
                        onChange={() => handleStationGroupChange(group.name)}
                        value={group.name}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {group.name}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={group.stations.length}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {group.totalCases.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={group.avgCoverage}
                          sx={{
                            flex: 1,
                            height: 8,
                            borderRadius: 1,
                            backgroundColor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: group.avgCoverage >= 80 ? '#4caf50' : group.avgCoverage >= 50 ? '#ff9800' : '#f44336'
                            }
                          }}
                        />
                        <Typography variant="body2" sx={{ minWidth: 45 }}>
                          {Math.round(group.avgCoverage)}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleGroup(group.name);
                        }}
                      >
                        {expandedGroups[group.name] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                  </TableRow>

                  {/* Expandable station details row */}
                  <TableRow>
                    <TableCell colSpan={6} sx={{ p: 0, borderBottom: expandedGroups[group.name] ? undefined : 'none' }}>
                      <Collapse in={expandedGroups[group.name]}>
                        <Box sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
                          {group.stations.length > 0 ? (
                            <Box>
                              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                Stationen in {group.name}
                              </Typography>
                              {group.stations.map((station, sIdx) => (
                                <Box key={sIdx} sx={{ mb: 1.5 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                    <Typography variant="body2">
                                      {station.name}
                                    </Typography>
                                    <Chip
                                      label={`${station.percentage}%`}
                                      size="small"
                                      sx={{
                                        backgroundColor: station.color || '#1976d2',
                                        color: 'white',
                                        fontSize: '0.7rem',
                                        height: 20
                                      }}
                                    />
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <LinearProgress
                                      variant="determinate"
                                      value={station.percentage}
                                      sx={{
                                        flex: 1,
                                        height: 6,
                                        borderRadius: 1,
                                        '& .MuiLinearProgress-bar': {
                                          backgroundColor: station.color || '#1976d2'
                                        }
                                      }}
                                    />
                                    {station.caseCount && (
                                      <Typography variant="caption" color="textSecondary" sx={{ minWidth: 80 }}>
                                        {station.caseCount.toLocaleString()} Fälle
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
                              Keine Stationen erfüllen das Mindestkriterium von 30 Fällen (Datenschutz)
                            </Alert>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Section 2: Nutzungs-Charakteristik */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          position: 'relative',
          ...(selectedStationGroup && {
            borderLeft: '4px solid #1976d2',
            backgroundColor: '#fafbff'
          })
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <BarChartIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Nutzungs-Charakteristik
          </Typography>
          {selectedStationGroup && (
            <Chip
              label={selectedStationGroup}
              color="primary"
              size="small"
              onDelete={() => handleStationGroupChange(null)}
              sx={{ ml: 'auto' }}
            />
          )}
          {isLoadingData && <CircularProgress size={20} sx={{ ml: 1 }} />}
        </Box>

        <Typography variant="body2" color="textSecondary" paragraph>
          {selectedStationGroup
            ? `Daten basieren auf: ${selectedStationGroup}`
            : 'Daten basieren auf allen Bereichen (aggregiert)'}
        </Typography>

        <Fade in={!isLoadingData} timeout={300}>
          <Grid container spacing={3}>
          {/* Messfrequenz */}
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TimelineIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2" fontWeight="bold">
                  Messfrequenz
                </Typography>
              </Box>

              {measurementPattern.typical ? (
                <Box>
                  {/* Key Stats in compact cards */}
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={4}>
                      <Box sx={{ p: 1, backgroundColor: 'white', borderRadius: 1, textAlign: 'center' }}>
                        <Typography variant="caption" color="textSecondary" display="block">
                          Häufigste
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {measurementPattern.typical}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ p: 1, backgroundColor: 'white', borderRadius: 1, textAlign: 'center' }}>
                        <Typography variant="caption" color="textSecondary" display="block">
                          Median
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {measurementPattern.avgInterval || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ p: 1, backgroundColor: 'white', borderRadius: 1, textAlign: 'center' }}>
                        <Typography variant="caption" color="textSecondary" display="block">
                          Streuung
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {measurementPattern.stdDev || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Visual distribution */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                      Verteilung der Messintervalle
                    </Typography>
                    {getFilteredMeasurementFrequency(selectedStationGroup).map((interval, idx) => (
                      <Box key={idx} sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.3 }}>
                          <Typography variant="caption" sx={{ minWidth: 45 }}>
                            {interval.label}
                          </Typography>
                          <Box sx={{ flex: 1, mx: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={interval.percent}
                              sx={{
                                height: 8,
                                borderRadius: 1,
                                backgroundColor: '#e0e0e0',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: interval.color,
                                  borderRadius: 1
                                }
                              }}
                            />
                          </Box>
                          <Typography variant="caption" fontWeight="bold" sx={{ minWidth: 35, textAlign: 'right' }}>
                            {interval.percent}%
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>

                  {measurementPattern.interpretation && (
                    <Alert severity="info" sx={{ mt: 2, fontSize: '0.85rem' }}>
                      {measurementPattern.interpretation}
                    </Alert>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  Keine Daten verfügbar
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Messungen pro Fall */}
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <BarChartIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2" fontWeight="bold">
                  Messungen pro Fall
                </Typography>
                <Tooltip title={`Statistik basiert auf ${casesWithParameter.toLocaleString()} Fällen mit mindestens einer Messung`}>
                  <IconButton size="small">
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Key Stats */}
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                      Median
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {medianMeasurementsPerCase}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                      Durchschn.
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {avgMeasurementsPerCase}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                      Gesamt
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {(totalMeasurements / 1000000).toFixed(1)}M
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Categorized distribution */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                  Verteilung nach Anzahl
                </Typography>
                {getFilteredMeasurementsPerCase(selectedStationGroup).map((category, idx) => {
                  const count = Math.round(casesWithParameter * (category.percent / 100));
                  return (
                  <Box key={idx} sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.3 }}>
                      <Typography variant="caption" sx={{ minWidth: 110 }}>
                        {category.label}
                      </Typography>
                      <Box sx={{ flex: 1, mx: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={category.percent}
                          sx={{
                            height: 8,
                            borderRadius: 1,
                            backgroundColor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: category.color,
                              borderRadius: 1
                            }
                          }}
                        />
                      </Box>
                      <Typography variant="caption" fontWeight="bold" sx={{ minWidth: 50, textAlign: 'right' }}>
                        {category.percent}% ({count.toLocaleString()})
                      </Typography>
                    </Box>
                  </Box>
                  );
                })}
              </Box>

              <Alert severity="info" sx={{ fontSize: '0.75rem', py: 0.5 }}>
                {continuousCasePercentage}% der Fälle haben ≥10 Messungen (kontinuierlich)
              </Alert>
            </Box>
          </Grid>
        </Grid>
        </Fade>

        <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mt: 2 }}>
          <Typography variant="caption">
            <strong>Methodologie:</strong> Alle Statistiken basieren auf Fällen mit mindestens einer Messung ({casesWithParameter.toLocaleString()} Fälle).
            Messfrequenz-Statistiken beziehen sich auf {stationGroups[0]?.name || 'die Hauptnutzungsbereiche'}.
          </Typography>
        </Alert>
      </Paper>

      {/* Section 3: Datenqualität */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          position: 'relative',
          ...(selectedStationGroup && {
            borderLeft: '4px solid #1976d2',
            backgroundColor: '#fafbff'
          })
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
          <CheckCircleIcon color="success" />
          <Typography variant="h6" fontWeight="bold">
            Datenqualität & Validierung
          </Typography>
          {selectedStationGroup && (
            <Chip
              label={selectedStationGroup}
              color="primary"
              size="small"
              onDelete={() => handleStationGroupChange(null)}
              sx={{ ml: 'auto' }}
            />
          )}
          {isLoadingData && <CircularProgress size={20} sx={{ ml: 1 }} />}
        </Box>

        <Fade in={!isLoadingData} timeout={300}>
          <Box>
            {/* Vollständigkeit & Plausibilität - Kompakter */}
            <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight="bold" fontSize="0.875rem">
                      Vollständigkeit
                    </Typography>
                    <Chip
                      label={`${getFilteredDataQuality(selectedStationGroup).completeness.value}%`}
                      size="small"
                      color="success"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={getFilteredDataQuality(selectedStationGroup).completeness.value}
                    sx={{
                      mb: 0.5,
                      height: 6,
                      borderRadius: 1,
                      '& .MuiLinearProgress-bar': { backgroundColor: '#4caf50' }
                    }}
                  />
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    <Chip
                      label={`${getFilteredDataQuality(selectedStationGroup).completeness.missing}% Missing (${getFilteredDataQuality(selectedStationGroup).completeness.pattern})`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.65rem' }}
                    />
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight="bold" fontSize="0.875rem">
                      Plausibilität
                    </Typography>
                    <Chip
                      label={`${getFilteredDataQuality(selectedStationGroup).plausibility.value}%`}
                      size="small"
                      color="success"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={getFilteredDataQuality(selectedStationGroup).plausibility.value}
                    sx={{
                      mb: 0.5,
                      height: 6,
                      borderRadius: 1,
                      '& .MuiLinearProgress-bar': { backgroundColor: '#4caf50' }
                    }}
                  />
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    <Chip label="90-140 mmHg" size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                    <Chip
                      label={`${getFilteredDataQuality(selectedStationGroup).plausibility.outsideRange}% außerhalb`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.65rem' }}
                    />
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Einheiten - Verteilung in den Rohdaten */}
            <Box sx={{ mb: 2.5, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                <InfoOutlinedIcon fontSize="small" color="primary" />
                <Typography variant="caption" fontWeight="bold">Einheiten in den Daten:</Typography>
                {getFilteredDataQuality(selectedStationGroup).units.map((unitData, idx) => (
                  <Chip
                    key={idx}
                    label={`${unitData.unit} (${unitData.percent}%)`}
                    size="small"
                    color={unitData.isPrimary ? "primary" : "default"}
                    variant={unitData.isPrimary ? "filled" : "outlined"}
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: unitData.isPrimary ? 'bold' : 'normal',
                      ...(unitData.isPrimary ? {} : { borderColor: '#1976d2', color: '#1976d2' })
                    }}
                  />
                ))}
              </Box>
              <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                Konversion erforderlich: kPa→mmHg (×7.5), cmH₂O→mmHg (÷1.36)
              </Typography>
            </Box>

            {/* Outlier - Kompakter */}
            <Box sx={{ mb: 2.5 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.875rem' }}>
                <WarningAmberIcon fontSize="small" color="warning" />
                Outlier-Behandlung
              </Typography>
              <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#fffbf0' }}>
                <Alert severity="info" icon={false} sx={{ mb: 1.5, fontSize: '0.7rem', py: 0.5, backgroundColor: 'transparent' }}>
                  <strong>Wichtig:</strong> Extremwerte NICHT entfernt – klinisch relevant bei Sepsis, Schock & hypertensiven Krisen.
                </Alert>

                <Grid container spacing={1}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ p: 1, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0', textAlign: 'center' }}>
                      <Typography variant="caption" color="textSecondary" display="block">IQR-Methode</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {getFilteredDataQuality(selectedStationGroup).outliers.iqr.percent}%
                      </Typography>
                      <Typography variant="caption" color="textSecondary" fontSize="0.65rem">
                        {'<'}{getFilteredDataQuality(selectedStationGroup).outliers.iqr.lower} | {'>'}{getFilteredDataQuality(selectedStationGroup).outliers.iqr.upper} mmHg
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ p: 1, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0', textAlign: 'center' }}>
                      <Typography variant="caption" color="textSecondary" display="block">Z-Score</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {getFilteredDataQuality(selectedStationGroup).outliers.zscore.percent}%
                      </Typography>
                      <Typography variant="caption" color="textSecondary" fontSize="0.65rem">
                        {'<'}{getFilteredDataQuality(selectedStationGroup).outliers.zscore.lower} | {'>'}{getFilteredDataQuality(selectedStationGroup).outliers.zscore.upper} mmHg
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ p: 1, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0', textAlign: 'center' }}>
                      <Typography variant="caption" color="textSecondary" display="block">Physiologisch</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {getFilteredDataQuality(selectedStationGroup).outliers.physiological.percent}%
                      </Typography>
                      <Typography variant="caption" color="textSecondary" fontSize="0.65rem">
                        {'<'}{getFilteredDataQuality(selectedStationGroup).outliers.physiological.lower} | {'>'}{getFilteredDataQuality(selectedStationGroup).outliers.physiological.upper} mmHg
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          </Box>
        </Fade>

        {/* Zeitliche Konsistenz - Kompakte Timeline */}
        <Box>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.875rem' }}>
            <InfoOutlinedIcon fontSize="small" color="primary" />
            Systemhistorie
          </Typography>
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CheckCircleIcon color="success" fontSize="small" />
                <Typography variant="caption" fontWeight="bold">2020+:</Typography>
                <Typography variant="caption" color="textSecondary">Philips IntelliVue</Typography>
              </Box>
              <Divider orientation="vertical" flexItem sx={{ height: 20, alignSelf: 'center' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <WarningAmberIcon color="warning" fontSize="small" />
                <Typography variant="caption" fontWeight="bold">{'<2020:'}</Typography>
                <Typography variant="caption" color="textSecondary">GE Datex (5er-Rundung)</Typography>
              </Box>
            </Box>
            <Alert severity="warning" sx={{ fontSize: '0.7rem', py: 0.5 }}>
              <strong>Systemwechsel 01/2020:</strong> Altsystem rundete auf 5er-Schritte. Varianzzunahme in 2018-2022
              Analysen ist Messartefakt, keine klinische Änderung.
            </Alert>
          </Paper>
        </Box>
      </Paper>

      {/* Section 4: Parameter mit ähnlicher Erfassung */}
      <Paper
        sx={{
          p: 3,
          position: 'relative',
          ...(selectedStationGroup && {
            borderLeft: '4px solid #1976d2',
            backgroundColor: '#fafbff'
          })
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <SearchIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Parameter mit ähnlicher Erfassung
          </Typography>
          <Tooltip title="Parameter mit ähnlicher Messfrequenz, Stationsnutzung und Erfassungsmuster">
            <IconButton size="small">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {selectedStationGroup && (
            <Chip
              label={selectedStationGroup}
              color="primary"
              size="small"
              onDelete={() => handleStationGroupChange(null)}
              sx={{ ml: 'auto' }}
            />
          )}
          {isLoadingData && <CircularProgress size={20} sx={{ ml: 1 }} />}
        </Box>

        <Typography variant="body2" color="textSecondary" paragraph>
          Diese Parameter werden mit ähnlicher Häufigkeit und auf ähnlichen Stationen erfasst (unabhängig vom medizinischen Inhalt)
        </Typography>

        <Fade in={!isLoadingData} timeout={300}>
          <Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'name'}
                        direction={sortBy === 'name' ? sortOrder : 'asc'}
                        onClick={() => handleSortRequest('name')}
                      >
                        Parameter
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Ähnlichkeitsgründe</TableCell>
                    <TableCell align="center">
                      <TableSortLabel
                        active={sortBy === 'caseCoverage'}
                        direction={sortBy === 'caseCoverage' ? sortOrder : 'asc'}
                        onClick={() => handleSortRequest('caseCoverage')}
                      >
                        Abdeckung
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">
                      <TableSortLabel
                        active={sortBy === 'frequency'}
                        direction={sortBy === 'frequency' ? sortOrder : 'asc'}
                        onClick={() => handleSortRequest('frequency')}
                      >
                        Frequenz
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">
                      <TableSortLabel
                        active={sortBy === 'stationCount'}
                        direction={sortBy === 'stationCount' ? sortOrder : 'asc'}
                        onClick={() => handleSortRequest('stationCount')}
                      >
                        Stationen
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">
                      <TableSortLabel
                        active={sortBy === 'similarityScore'}
                        direction={sortBy === 'similarityScore' ? sortOrder : 'asc'}
                        onClick={() => handleSortRequest('similarityScore')}
                      >
                        Ähnlichkeit
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortSimilarParameters(getFilteredSimilarParameters(selectedStationGroup)).map((param, idx) => (
                <TableRow
                  key={idx}
                  hover
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#f5f5f5'
                    }
                  }}
                  onClick={() => navigate(`/parameter/${param.id}`)}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {param.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {param.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {param.similarityReasons.map((reason, rIdx) => (
                        <Chip
                          key={rIdx}
                          label={reason}
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={`${param.caseCoverage}%`}
                      size="small"
                      sx={{
                        backgroundColor: '#e3f2fd',
                        fontSize: '0.75rem'
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                      <TimelineIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {param.frequency}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                      <GroupIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {param.stationCount}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 50,
                        height: 50,
                        borderRadius: '50%',
                        backgroundColor: param.similarityScore >= 80 ? '#e8f5e9' : param.similarityScore >= 60 ? '#fff3e0' : '#f5f5f5',
                        border: `2px solid ${param.similarityScore >= 80 ? '#4caf50' : param.similarityScore >= 60 ? '#ff9800' : '#bdbdbd'}`
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={param.similarityScore >= 80 ? 'success.main' : param.similarityScore >= 60 ? 'warning.main' : 'textSecondary'}
                      >
                        {param.similarityScore}%
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mt: 2 }}>
          <Typography variant="caption">
            <strong>Hinweis:</strong> Die Ähnlichkeit bezieht sich auf die <strong>Erfassungseigenschaften</strong> (Messfrequenz, Stationsnutzung, Erfassungsmuster),
            nicht auf die medizinische Bedeutung. Parameter wie Blutdruck und Herzfrequenz können ähnlich erfasst werden, obwohl sie medizinisch unterschiedliche Sachverhalte beschreiben.
          </Typography>
        </Alert>
          </Box>
        </Fade>
      </Paper>
    </Box>
  );
};

export default DataExplorationDetail;
